import { z } from 'zod';
/**
 * The pipeline extract: every deal, in full, split across MEFOSA's own three
 * boards — Follow ups, Under implementation, Closed.
 *
 * Deliberately NOT the same thing as the per-user entry extract next to it.
 * That one answers "what has this colleague put into the system", one sheet
 * per kind of record, and its Deals sheet carries eleven columns. This one
 * answers "show me the pipeline in full", every field a deal has, arranged the
 * way MEFOSA's own workbook arranges it. Nemer asked for both, a week apart,
 * and folding either into the other would make it worse at its own job.
 *
 * Reuses EntrySheet/EntryCell so money stays a tagged object (rule 5) and the
 * workbook writer is shared rather than re-implemented.
 */
/**
 * Which board a sheet represents.
 *
 * These mirror `stageBoard()` exactly, because which board a deal sits on is
 * DERIVED from its stage and never stored (rule 8). The report must not invent
 * a second answer to that question.
 */
export const PIPELINE_BOARDS = ['follow_up', 'implementation', 'closed'];
/** What the extract cannot tell you, printed on the cover rather than hidden. */
export const PIPELINE_CAVEATS = [
    /**
     * The date range filters by when a deal was ENTERED, not when it was worked
     * or closed. A deal entered in January and closed in June appears in a
     * January report, on the Closed board, with June's figures.
     */
    'enteredNotClosed',
    /**
     * `created_by` is nullable, and imports stamp the UPLOADER as the creator of
     * every row. Filtering by a person therefore over-credits whoever committed
     * a spreadsheet and misses anything written outside the routes.
     */
    'importsAndUnattributed',
    /** Deals entered then removed are included and marked, not silently dropped. */
    'removedIncluded',
    /** Profit is revenue minus cost, computed for the sheet and never stored. */
    'profitDerived',
];
export const pipelineReportQuerySchema = z
    .object({
    from: z.string().date(),
    to: z.string().date(),
    /**
     * Optional, unlike the per-user extract where it is required. The default
     * here is the whole pipeline; narrowing to one colleague is a filter on
     * top, which is the opposite of a report that is meaningless without a
     * named subject.
     */
    userId: z.string().uuid().optional(),
    /**
     * Who the deal BELONGS to — their sheet's Sales Person column.
     *
     * A different question from userId, and the more useful one today:
     * every deal was imported by the admin account, so "entered by" says
     * admin for all 121 of them while the owner is the salesperson who
     * actually ran the deal.
     */
    ownerId: z.string().uuid().optional(),
})
    .refine((v) => v.from <= v.to, {
    message: 'The start date must be on or before the end date',
    path: ['from'],
});
//# sourceMappingURL=pipelineReport.js.map